import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    update,
} from '../services/ApplicationsService';

import {Application} from '../types';

// FNP-947: records are React Query's job; selection stays in Redux (FNP-951).
//
// Note this service already returns `listApplications.items` (a bare array),
// unlike accounts and account-applications, whose services return the whole
// connection object. No unwrap needed here.

export const applicationsKeys = {
    all: ['applications'] as const,
    lists: () => [...applicationsKeys.all, 'list'] as const,
    list: (filter: object) => [...applicationsKeys.lists(), filter] as const,
    details: () => [...applicationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...applicationsKeys.details(), id] as const,
};

export const useApplicationsQuery = (filter: object = {}) => {
    return useQuery({
        queryKey: applicationsKeys.list(filter),
        queryFn: async (): Promise<Application[]> => {
            const records = await fetchRecords(filter);
            return records ?? [];
        },
    });
};

export const useApplicationQuery = (applicationId?: string) => {
    return useQuery({
        queryKey: applicationsKeys.detail(applicationId ?? ''),
        queryFn: () => fetchOne(applicationId as string),
        enabled: !!applicationId,
    });
};

export const useCreateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: Application) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: applicationsKeys.lists()});
        },
    });
};

export const useUpdateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: Application }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: applicationsKeys.lists()});
            queryClient.invalidateQueries({queryKey: applicationsKeys.detail(variables.id)});
        },
    });
};

export const useDeleteApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: applicationsKeys.lists()});
        },
    });
};
